import { FileDown, Loader2, Minus, Plus } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Tip } from "@/components/ui/overlays";
import { useMarkup, useMarkupConfig } from "@/hooks/queries";
import { api } from "@/lib/api";
import { useAnnotationTool } from "@/stores/annotation-tool";
import { useShell } from "@/stores/shell";

import { AnnotationToolbar } from "./AnnotationToolbar";
import { drawPageAnnotations } from "./annotation-overlay";
import { loadFabric, PageEditor, type EditorOpts, type PersistHandlers } from "./fabric-editor";
import {
  editorOpts,
  useAnnotationStyle,
  useEditorToolSync,
} from "./use-editor-tool-sync";
import { inkExtents, roamBounds, TILE_H, TILE_W, type Extent } from "./infinite-canvas-math";
import type { InkModel } from "./ink-model";
import { fingerDrawActive } from "./input-policy";
import { isLightPaper, paperBackground, type PaperStyle } from "./paper";
import { isInkTool, PenDownBuffer } from "./pen-buffer";
import { useViewportGestures } from "./use-viewport-gestures";
import { clamp, ViewportController } from "./viewport-controller";
import { flushViewState, loadViewState, rememberedTool, saveViewState, type ReaderViewState } from "./view-state";
import { useViewportScrollbars } from "./viewport-scrollbars";

// The loaded Fabric module type, without importing `fabric` at module scope.
type Fabric = Awaited<ReturnType<typeof loadFabric>>;

const MIN_SCALE = 0.1;
const MAX_SCALE = 4;
const INITIAL_SCALE = 0.5;
// Dot-grid spacing in tile-pixel scene units; the CSS background-size/position scale
// with the camera for a cheap parallax (plan F7: "Background: CSS dot-grid").
const GRID_SCENE = 40;
// Screen px of grey left visible above and left of the corner when a new note
// opens — enough that the edges read as edges, not as the window's own border.
const SEAT_MARGIN = 28;
// How far the paper layer extends right and down, in scene units. The surface is
// unbounded that way, but the LAYER cannot be: it is one transformed div, and a
// pattern painted over an unbounded box would be. This is ~90 US-Letter pages in
// each direction — far past any real note, and cheap because the browser only
// rasterizes the part on screen.
const PAPER_EXTENT = 200_000;

/** OneNote-style INFINITE canvas (plan F7). NOT a giant page: ONE PageEditor bound
 * to page 0 whose canvas fills the container, and the ViewportController IS the
 * camera — its tx/ty/scale become Fabric's viewportTransform (never a CSS transform
 * of the fixed canvas). Reuses the same controller/editor/model/pen-buffer/scrollbar
 * machinery the paged CanvasView uses; only the orchestration differs (one camera
 * surface, unbounded tile-normalized ink on page 0, no page chrome). */
export function InfiniteCanvasView({
  docId,
  background,
  persist,
  model,
  paper,
  spacing = 1,
  title,
}: {
  docId: string;
  background: string;
  persist: PersistHandlers;
  /** The per-document InkModel (north-star C): the single editor seeds from it, the
   * read-only overlay renders from it, and its events route to the editor. */
  model: InkModel;
  /** Paper style behind the ink — the dot-grid becomes paper-aware (plan §3.5),
   * keeping the camera parallax. Already normalized by CanvasView; an infinite
   * canvas defaults null/undefined ⇒ "dots" (F7's signature look), so this is only
   * "blank" when the user explicitly chose it. */
  paper: PaperStyle;
  /** Cell spacing as a multiple of the surface's natural unit — the note's own
   * setting (see the Paper control in ReaderTab). */
  spacing?: number;
  /** The note's name, shown in this row while fullscreen (see CanvasView). */
  title?: string;
}) {
  const fullscreen = useShell((s) => s.fullscreen);
  const markup = useMarkup(docId);
  const config = useMarkupConfig();
  const editable = config.data?.enabled ?? false;
  const fonts = config.data?.fonts ?? [];

  // Restore the per-doc view state (F8) once: the saved camera (tx/ty/scale) is
  // seated on first layout. The tool is remembered but NOT auto-applied — the doc
  // opens read-mode; the saved tool only feeds the pen-down auto-activate (F1.4).
  const restoreRef = useRef<ReaderViewState | null | undefined>(undefined);
  if (restoreRef.current === undefined) restoreRef.current = loadViewState(docId);
  const initialScale = restoreRef.current ? clamp(restoreRef.current.zoom, MIN_SCALE, MAX_SCALE) : INITIAL_SCALE;

  const [displayScale, setDisplayScale] = useState(initialScale);
  const [saving, setSaving] = useState(false);

  // One subscription set, shared by all three host surfaces.
  const style = useAnnotationStyle();
  const { tool } = style;
  const editMode = editable && tool !== "none";
  const editModeRef = useRef(editMode);
  editModeRef.current = editMode;
  const editableRef = useRef(editable);
  editableRef.current = editable;

  const modelRef = useRef(model);
  modelRef.current = model;
  // The chosen paper, read live by the per-frame camera writer (applyCameraCss).
  const paperRef = useRef(paper);
  paperRef.current = paper;
  // Mount the editor only once the first fetch has settled + seeded the model, so the
  // single editor never seeds empty (same guard the paged view uses).
  const markupReady = !markup.isLoading;

  const containerRef = useRef<HTMLDivElement | null>(null);
  // The fixed Fabric host (fills the container); the camera moves via the editor's
  // viewportTransform, NOT this element.
  const hostRef = useRef<HTMLDivElement | null>(null);
  // The read-only overlay's "world": a CSS-camera-transformed div (no Fabric here,
  // so a cheap transform is fine) holding a tile-sized, overflow-visible overlay host.
  const worldRef = useRef<HTMLDivElement | null>(null);
  const overlayHostRef = useRef<HTMLDivElement | null>(null);

  const editorRef = useRef<PageEditor | null>(null);
  const fabricRef = useRef<Fabric | null>(null);
  const controllerRef = useRef<ViewportController | null>(null);
  const penBufferRef = useRef<PenDownBuffer | null>(null);
  if (!penBufferRef.current) penBufferRef.current = new PenDownBuffer();
  // Cached ink extents (tile-px), refreshed inside recomputeContent (cheap at
  // canvas-note object counts) so the roam bounds track the ink.
  const inkExtentRef = useRef<Extent | null>(null);
  // The first layout SEATS the camera (restore or centre) before content bounds
  // exist, so the seat lands exactly; afterwards the bounds gate the clamp.
  const seatPendingRef = useRef(true);

  const optsRef = useRef<EditorOpts>(editorOpts(style));
  optsRef.current = editorOpts(style);

  // The paper layer: a camera-transformed div anchored at scene (0,0).
  const paperRef2 = useRef<HTMLDivElement | null>(null);
  // Live values the (stable) paper painter reads without re-subscribing.
  const backgroundRef = useRef(background);
  backgroundRef.current = background;
  const spacingRef = useRef(spacing);
  spacingRef.current = spacing;
  // The scale the paper's line width was last derived at, plus the settle timer.
  const lineScaleRef = useRef(0);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Paint the paper layer's colour + pattern. Called on a paper/background/
   * spacing change and when a zoom settles — never per frame. */
  const repaintPaper = useCallback(() => {
    const paper = paperRef2.current;
    const c = controllerRef.current;
    if (!paper || !c) return;
    paper.style.backgroundColor = backgroundRef.current;
    // Cell in SCENE units (constant across zoom); line width in scene units too,
    // chosen so it renders about one CSS pixel at the CURRENT scale.
    const unit = GRID_SCENE * spacingRef.current;
    const bg = paperBackground(paperRef.current, unit, 1 / Math.max(0.05, c.scale));
    if (bg) {
      paper.style.backgroundImage = bg.backgroundImage;
      paper.style.backgroundSize = bg.backgroundSize;
      paper.style.backgroundPosition = "0 0";
    } else {
      paper.style.backgroundImage = "none";
    }
  }, []);

  const { updateScrollbars, scrollbars } = useViewportScrollbars(controllerRef);

  // The paper's own settings changed (style, spacing, colour) — repaint the
  // layer. Instant: it is one style write, no fetch and no re-render.
  useEffect(() => {
    repaintPaper();
  }, [paper, spacing, background, repaintPaper]);

  // Redraw the read-only overlay (cleared while editing — Fabric owns the surface).
  // The overlay host is tile-sized and overflow-visible, so ink far outside [0,1]
  // still renders; the world div's CSS camera transform places it on screen.
  const drawOverlays = useCallback(() => {
    const host = overlayHostRef.current;
    if (!host) return;
    try {
      drawPageAnnotations(host, editModeRef.current ? [] : modelRef.current.byPage(0));
      const svg = host.querySelector(".annotation-overlay") as SVGElement | null;
      if (svg) svg.style.overflow = "visible"; // show ink beyond the tile box
    } catch {
      /* markup must never break the canvas */
    }
  }, []);

  // Write the camera onto the read-overlay world AND the paper layer (both are
  // one CSS transform). Cheap; called every frame from the controller's onChange.
  const applyCameraCss = useCallback((tx: number, ty: number, scale: number) => {
    const world = worldRef.current;
    if (world) world.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    // The PAPER rides the same camera transform. It is one write per frame, and
    // — critically — the pattern's cell is constant in SCENE units, so the
    // browser never re-rasterizes the repeating gradient while you pinch; the
    // compositor scales the already-painted layer. Recomputing background-size
    // per frame is what made the grid crawl and shimmer, because a 1px hard stop
    // lands on a different sub-pixel boundary at every fractional size.
    //
    // The layer starts at scene (0,0) — the writable corner — so panning up or
    // left simply moves it away and reveals the grey surround behind.
    const paper = paperRef2.current;
    if (paper) paper.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    // A settled zoom re-derives the LINE WIDTH so it is ~1 CSS px at rest (the
    // transform would otherwise leave it scaled). One write per gesture.
    if (scale !== lineScaleRef.current) {
      lineScaleRef.current = scale;
      if (settleRef.current) clearTimeout(settleRef.current);
      settleRef.current = setTimeout(() => repaintPaper(), 120);
    }
  }, []);

  // Refresh the roam bounds from the current ink extents ∪ the visible scene rect,
  // padded by a generous margin so a pan is never trapped (unioning the VIEW means
  // each pan expands the frontier a viewport further) while the scrollbars stay
  // meaningful. Synchronous + cheap so a big pan keeps expanding immediately.
  const recomputeContent = useCallback(() => {
    const c = controllerRef.current;
    const root = containerRef.current;
    if (!c || !root) return;
    inkExtentRef.current = inkExtents(modelRef.current.byPage(0), TILE_W, TILE_H);
    const vw = root.clientWidth;
    const vh = root.clientHeight;
    const tl = c.screenToWorld(0, 0);
    const br = c.screenToWorld(vw, vh);
    const view: Extent = { minX: tl.x, minY: tl.y, maxX: br.x, maxY: br.y };
    const marginX = Math.max(vw / c.scale, TILE_W);
    const marginY = Math.max(vh / c.scale, TILE_H);
    const { ox, oy, w, h } = roamBounds(inkExtentRef.current, view, marginX, marginY);
    c.setContentBounds(ox, oy, w, h);
  }, []);

  // Construct the controller once. onChange (called synchronously on every transform
  // change) drives the Fabric camera + the CSS grid/overlay immediately, expands the
  // roam bounds, and rAF-throttles the % readout + scrollbars + view-state persist.
  if (!controllerRef.current) {
    let raf = 0;
    controllerRef.current = new ViewportController({
      min: MIN_SCALE,
      max: MAX_SCALE,
      onChange: () => {
        const c = controllerRef.current;
        if (!c) return;
        editorRef.current?.setCamera(c.tx, c.ty, c.scale);
        applyCameraCss(c.tx, c.ty, c.scale);
        // Skip while seating: the first layout sets the camera before content bounds
        // exist so the seat isn't clamped; bounds are recomputed once seated.
        if (!seatPendingRef.current) recomputeContent();
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = 0;
          const cur = controllerRef.current;
          if (!cur) return;
          setDisplayScale(cur.scale);
          updateScrollbars();
          saveViewState(docId, {
            zoom: cur.scale, tx: cur.tx, ty: cur.ty, tool: optsRef.current.tool, updatedAt: Date.now(),
          });
        });
      },
    });
  }

  // Arm the pen-down buffer for the whole annotatable session (F1.3 + F1.4): in read
  // mode the first PEN contact auto-activates the remembered tool (the whole surface
  // is inkable, so no page-margin gate is needed); in edit mode it bridges the
  // first-edit dead zone until the single editor attaches.
  const armPenBuffer = () => {
    const container = containerRef.current;
    const buffer = penBufferRef.current;
    if (!container || !buffer || !editableRef.current) return;
    buffer.arm(container, {
      shouldBuffer: () => isInkTool(optsRef.current.tool),
      autoActivate: {
        shouldActivate: () => optsRef.current.tool === "none",
        onActivate: () => useAnnotationTool.getState().setTool(rememberedTool(docId)),
      },
    });
  };

  useViewportGestures(containerRef, controllerRef, {
    shouldFingerDraw: () => fingerDrawActive(editModeRef.current),
    // Two/three-finger tap = undo/redo on tablets — the touch analogue of Ctrl+Z/Y,
    // gated like it (edit mode only, read live via refs).
    onTwoFingerTap: () => {
      if (editModeRef.current) modelRef.current.undo();
    },
    onThreeFingerTap: () => {
      if (editModeRef.current) modelRef.current.redo();
    },
  });

  // Attach the controller with NO CSS world (the camera drives Fabric's
  // viewportTransform + the read overlay's own transform), size it, and seat the
  // camera once — restoring the saved camera (F8) or centring the tile origin.
  useLayoutEffect(() => {
    const controller = controllerRef.current;
    const root = containerRef.current;
    if (!controller || !root) return;
    controller.attach(null); // no CSS-transformed world; onChange fans out the camera
    controller.setViewportSize(root.clientWidth, root.clientHeight);
    // Seat BEFORE content bounds exist (contentW == 0 ⇒ the clamp is inert), so the
    // restore/centre lands exactly; recompute the roam bounds right after.
    const restore = restoreRef.current || null;
    if (restore) {
      controller.setTransform(restore.tx, restore.ty, clamp(restore.zoom, MIN_SCALE, MAX_SCALE));
    } else {
      // A NEW note opens on its top-left CORNER, not the middle of the tile. On
      // an unbounded surface the middle looks identical to everywhere else — you
      // start with no idea which way anything is. Seating the corner just inside
      // the viewport puts both edges on screen from the first frame, so the
      // bounds are something you see rather than discover by panning into them.
      controller.setTransform(
        SEAT_MARGIN,
        SEAT_MARGIN,
        INITIAL_SCALE,
      );
    }
    seatPendingRef.current = false;
    recomputeContent();
    applyCameraCss(controller.tx, controller.ty, controller.scale);
    updateScrollbars();
    return () => {
      const c = controllerRef.current;
      if (c) {
        saveViewState(docId, {
          zoom: c.scale, tx: c.tx, ty: c.ty, tool: optsRef.current.tool, updatedAt: Date.now(),
        });
      }
      flushViewState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the viewport size + the editor's canvas size current (assistant collapsing,
  // rotating). Re-clamp after so content shifts stay on-screen.
  useEffect(() => {
    const root = containerRef.current;
    const controller = controllerRef.current;
    if (!root || !controller) return;
    const ro = new ResizeObserver(() => {
      // Center-anchored resize compensation (Part A mechanism 2): keep the world
      // point at the viewport centre fixed as a bar toggle resizes the container,
      // rather than letting the camera drift with the container edge. resizeViewport
      // sets the new size, re-anchors the centre, re-clamps, and re-renders (which
      // fans the camera out via onChange); resize the Fabric canvas to match first.
      editorRef.current?.resize(root.clientWidth, root.clientHeight);
      controller.resizeViewport(root.clientWidth, root.clientHeight);
      recomputeContent();
      applyCameraCss(controller.tx, controller.ty, controller.scale);
      updateScrollbars();
    });
    ro.observe(root);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Feed the saved-markup fetch into the model: SEED on the first load, RECONCILE
  // after (never removing local ink), then refresh the roam bounds + read overlay.
  useEffect(() => {
    if (markup.isLoading) return;
    const data = markup.data ?? [];
    if (!model.isSeeded()) model.seed(data);
    else model.reconcile(data);
    recomputeContent();
    updateScrollbars();
    drawOverlays();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markup.data, markup.isLoading, model]);

  // Route model events to the single page-0 editor while editing (an undo/redo or a
  // reconcile it didn't originate), or redraw the read overlay while reading; keep
  // the roam bounds fresh either way.
  useEffect(() => {
    const sync = () => {
      recomputeContent();
      updateScrollbars();
    };
    const offs = [
      model.on("added", (a) => {
        if (editModeRef.current) editorRef.current?.applyExternalAdd(a);
        else drawOverlays();
        sync();
      }),
      model.on("removed", (id) => {
        if (editModeRef.current) editorRef.current?.applyExternalRemove(id);
        else drawOverlays();
        sync();
      }),
      model.on("replaced", (a) => {
        if (editModeRef.current) editorRef.current?.applyExternalReplace(a);
        else drawOverlays();
        sync();
      }),
      model.on("idChanged", (tempId, serverId) => {
        if (editModeRef.current) editorRef.current?.applyIdChange(tempId, serverId);
      }),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  // Resolve the (single) editor for a buffered pen contact within the container.
  const editorAtPoint = useCallback((cx: number, cy: number): PageEditor | null => {
    const root = containerRef.current;
    const ed = editorRef.current;
    if (!root || !ed) return null;
    const r = root.getBoundingClientRect();
    return cx >= r.left && cx <= r.right && cy >= r.top && cy <= r.bottom ? ed : null;
  }, []);

  // Build the single page-0 editor filling the container (infinite mode), seeded from
  // the model, and sync it to the current camera. No-op once it exists.
  const ensureEditor = useCallback(() => {
    const fabric = fabricRef.current;
    const host = hostRef.current;
    const controller = controllerRef.current;
    if (!fabric || !host || !controller || editorRef.current) return;
    const editor = new PageEditor(
      fabric, host, 0, modelRef.current.byPage(0), persist, optsRef.current,
      { tileW: TILE_W, tileH: TILE_H },
    );
    editor.setCamera(controller.tx, controller.ty, controller.scale);
    editorRef.current = editor;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persist]);

  // Arm the pen-down buffer for the whole annotatable session — keyed on `editable`
  // only (NOT editMode), so the read→edit transition a pen-down triggers never
  // disarms the buffer out from under the stroke it just recorded.
  useEffect(() => {
    const container = containerRef.current;
    const buffer = penBufferRef.current;
    if (!editable || !container || !buffer) return;
    armPenBuffer();
    return () => buffer.disarm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editable]);

  // Edit-mode session: load Fabric once per entry, build the single editor, and
  // destroy it on exit/unmount. Gated on markupReady so it always seeds from loaded
  // data. The camera is NOT a dep: onChange re-sizes the living editor in place.
  useEffect(() => {
    if (!editMode || !markupReady) return;
    let cancelled = false;
    drawOverlays(); // clear the read overlay before Fabric takes over
    loadFabric()
      .then((fabric) => {
        if (cancelled) return;
        fabricRef.current = fabric;
        ensureEditor();
        penBufferRef.current?.attachEditors(editorAtPoint);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Couldn't load the annotation tools");
        useAnnotationTool.getState().setTool("none");
      });
    return () => {
      cancelled = true;
      fabricRef.current = null;
      editorRef.current?.destroy();
      editorRef.current = null;
      drawOverlays(); // restore the read overlay now Fabric is gone
      armPenBuffer(); // re-arm so a return to read mode can auto-activate
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, markupReady]);

  // Push tool/style changes to the live editor, one field per effect.
  useEditorToolSync(
    () => (editorRef.current ? [editorRef.current] : []),
    editMode,
    style,
  );

  // Read-only overlay redraw when edit mode toggles (clear on enter, restore on exit).
  useEffect(() => {
    drawOverlays();
  }, [editMode, drawOverlays]);

  const saveAsPdf = async () => {
    setSaving(true);
    try {
      const { file_url } = await api.request<{ file_url: string }>(
        "POST",
        `/api/documents/${docId}/canvas-pdf`,
      );
      window.open(file_url, "_blank");
      toast.success("Saved a PDF cropped to your note");
    } catch (e) {
      toast.error("Couldn't save the PDF", { description: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const zoomBy = (factor: number) => {
    const controller = controllerRef.current;
    if (controller) controller.zoomTo(controller.scale * factor);
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#1b1f1a]">
      {/* FIXED height + no-wrap: the AnnotationToolbar expands on annotate/tool
          switch and must scroll internally, never reflow the chrome height and
          shift the camera surface below it (Part A mechanism 1). */}
      <div className="hairline-b flex h-9 shrink-0 flex-nowrap items-center gap-2 px-3 py-1 text-muted-foreground">
        {/* Fullscreen collapses the document's title bar, so while writing there
            was nothing on screen naming the note. This row is the surface's own
            chrome and it is always visible, so the title moves here — the label
            it replaces is the least useful thing on the row. */}
        {fullscreen && title ? (
          <span className="min-w-0 truncate text-[12px] text-foreground/90" title={title}>
            {title}
          </span>
        ) : (
          <span className="shrink-0 font-mono text-[11px]">ink · ∞</span>
        )}
        {/* Next to the label, not at the right edge — the reader's floating
            document-controls island owns that corner and covers anything
            right-aligned here (see CanvasView for the long version). */}
        <div className="ml-3 flex items-center gap-1">
          <Tip label="Save as PDF (cropped to your note)">
            <Button variant="ghost" size="icon" onClick={() => void saveAsPdf()} disabled={saving}>
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
            </Button>
          </Tip>
          <Tip label="Zoom out">
            <Button variant="ghost" size="icon" onClick={() => zoomBy(1 / 1.2)} aria-label="Zoom out">
              <Minus className="h-3.5 w-3.5" />
            </Button>
          </Tip>
          <span className="w-10 text-center font-mono text-[10px] text-muted-foreground">{Math.round(displayScale * 100)}%</span>
          <Tip label="Zoom in">
            <Button variant="ghost" size="icon" onClick={() => zoomBy(1.2)} aria-label="Zoom in">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </Tip>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
      <div
        ref={containerRef}
        tabIndex={0}
        style={{ padding: 0, touchAction: "none" }}
        className={`pdf-scroll absolute inset-0 overflow-hidden bg-[#1b1f1a] outline-none${editMode ? " gv-editing" : ""}`}
      >
        {/* The PAPER: a camera-TRANSFORMED layer anchored at scene (0,0) — the
            writable corner — carrying both the note's colour and its pattern.
            Panning up or left moves it away and reveals the container's grey
            surround, which is the visible beginning of the page and the boundary
            the editor refuses to ink past. Being transformed (rather than
            repainted per frame) is what makes zooming smooth. */}
        <div
          ref={paperRef2}
          data-canvas-paper
          className="gv-canvas-grid pointer-events-none absolute"
          style={{
            left: 0,
            top: 0,
            width: PAPER_EXTENT,
            height: PAPER_EXTENT,
            transformOrigin: "0 0",
            willChange: "transform",
            background,
          }}
        />
        {/* Read-only overlay world: a CSS-camera-transformed div (no Fabric here) with
            a tile-sized, overflow-visible host so ink far outside [0,1] still shows. */}
        <div
          ref={worldRef}
          data-canvas-world
          className="gv-canvas-world"
          style={{ position: "absolute", left: 0, top: 0, transformOrigin: "0 0", willChange: "transform", pointerEvents: "none" }}
        >
          <div
            ref={overlayHostRef}
            style={{ position: "absolute", left: 0, top: 0, width: TILE_W, height: TILE_H, overflow: "visible", pointerEvents: "none" }}
          />
        </div>
        {/* The fixed Fabric host: fills the container; the camera moves via the
            editor's viewportTransform, not this element. */}
        <div ref={hostRef} data-canvas-host className="absolute inset-0" />
        {scrollbars}
        {markup.isLoading && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        )}
      </div>
        {editable && <AnnotationToolbar fonts={fonts} model={model} paper={isLightPaper(background) ? "light" : "dark"} />}
      </div>
    </div>
  );
}
